import React from 'react';
import classNames from 'classnames';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDice, faTimesCircle } from '@fortawesome/free-solid-svg-icons'

import Data from 'data';
import Store from 'store';
import Locale from 'data/locale';

import Accordion from 'ui/accordion';
import Tooltip from 'ui/tooltip';
import './Stats.scss';
import { selectStats } from 'logic/stats';
import { formatNumber, replaceReact } from 'utils/format';
import EditingContext from 'store/editingContext';
import TooltipBuilder, { colored } from 'tooltips/builder';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import statList from './statList';
import { Color } from 'common';
import { itemSlotGroups } from 'logic/itemTypes';
import { addValues, statsToLines } from 'logic/item';
import Select from 'ui/select';
import { breakpointList } from 'logic/skills/animation';
import shrineBuffs from 'logic/skills/shrines';
import { EmbedProfileLink, useContentCreator, useProfileSetId } from 'components/cclink';
import { OverlayContext } from 'ui/utils/OverlayManager';

const _L = Locale('ui-stats');

const sourceOrder = ['base', 'weapon', 'stats', 'quest', 'difficulty', 'skill'];
function sourceName(source, suffix='') {
  let m;
  switch (source) {
  case 'inventory': return colored(_L`Charms` + suffix, Color.GOLD2);
  case 'base': return colored(_L`Base Value` + suffix, Color.GRAY);
  case 'weapon': return colored(_L`Weapon` + suffix, Color.GRAY);
  case 'stats': return colored(_L`Attributes` + suffix, Color.GRAY);
  case 'items': return colored(_L`Items` + suffix, Color.GRAY);
  case 'difficulty': return colored(_L`Difficulty` + suffix, Color.GRAY);
  case 'quest': return colored(_L`Quests` + suffix, Color.GRAY);
  case 'player': return colored(_L`Player` + suffix, Color.GRAY);
  case 'mercenary': return colored(_L`Mercenary` + suffix, Color.GRAY);
  case 'monster': return colored(_L`Monster modifiers` + suffix, Color.GRAY);
  case 'targetdamage': return colored(_L`Damage vs. Monster Type` + suffix, Color.GRAY);
  case 'convert': return colored(_L`Converted Damage` + suffix, Color.GRAY);
  case 'skill': return colored(_L`Skill Bonus` + suffix, Color.GRAY);
  default:
    if ((m = source.match(/^skill(\d+)$/))) {
      const skill = Data.skills[m[1]];
      const desc = Data.skillDesc[skill?.skilldesc];
      return colored(((desc && Locale.strings[desc.strname]) || skill.skill) + suffix, Color.ORANGE);
    }
    if ((m = source.match(/^set\.(.*)$/))) {
      const set = Data.sets[m[1]];
      if (set) return colored((Locale.strings[set.name] || set.name) + suffix, Color.GREEN);
    }
    if (shrineBuffs[source]) {
      return colored(Locale.strings[shrineBuffs[source].name] + suffix, Color.YELLOW);
    }
    if (Data.itemStatCost[source] || source.includes('#')) return;
    return colored((_L(itemSlotGroups[source]?.name) || _L`Unknown`) + suffix, Color.GOLD2);
  }
}

export function formatValue(stat, value, stats) {
  let text;
  if (value && typeof value === "object" && value.min === value.max) {
    value.min = value.max;
  }
  const decimal = (typeof stat.decimal === 'function' ? stat.decimal(stats) : stat.decimal) || 0;
  if (typeof value === "number") {
    text = formatNumber(value, decimal || 0, 10000);
    if (stat.plus && value > 0) text = "+" + text;
  } else if (value) {
    if (value.min === value.max) {
      text = formatNumber(value.min, decimal, 10000);
    } else {
      text = formatNumber(value.min, decimal, 10000) + '\u2013' + formatNumber(value.max, decimal, 10000);
    }
    if (stat.plus && value.min > 0) text = "+" + text;
  } else {
    return '0';
  }
  if (stat.percent) text += "%";
  return text;
}

const minval = x => (x && typeof x === 'object') ? x.min : x;

function getSources(dst, stats, text) {
  const [, domain, code1, code2, length] = text.match(/^(?:(\w+)\.)?([\w#]+)(?:-([\w#]+))?(?:\/(\w+))?$/) || [];
  const divide = parseInt(length);
  if (domain) stats = stats[domain];
  if (!stats) return dst;
  if (code2) {
    let lhs = stats.sources[code1], rhs = stats.sources[code2];
    if (!lhs && !rhs) return dst;
    if (!lhs) lhs = {};
    if (!rhs) rhs = {};
    const keys = [...new Set([...Object.keys(lhs), ...Object.keys(rhs)])];
    for (const id of keys) {
      let min = lhs[id] || 0, max = rhs[id] || 0;
      if (!isNaN(divide)) {
        min /= divide;//Math.round(min / divide);
        max /= divide;//Math.round(max / divide);
      } else if (length) {
        const len = (stats.sources[length]?.[id] || 0) / (stats.sources.poison_count?.[id] || 1);
        if (!len) continue;
        min = min * len / 256;//Math.round(min * len / 256);
        max = max * len / 256;//Math.round(max * len / 256);
      }
      dst[id] = addValues(dst[id], {min, max});
    }
  } else {
    const src = stats.sources[code1];
    if (!src) return dst;
    for (const id in src) {
      let value = src[id] || 0;
      if (!isNaN(divide)) {
        value /= divide;//Math.round(value / divide);
      } else if (length) {
        const len = (stats.sources[length]?.[id] || 0) / (stats.sources.poison_count?.[id] || 1);
        if (!len) continue;
        value = value * len / 256;//Math.round(value * len / 256);
      }
      dst[id] = addValues(dst[id], src[id]);
    }
  }
  return dst;
}

export function StatTooltip({ stat, stats, allStats, skills, unit }) {
  const [tooltip, sources] = React.useMemo(() => {
    if (!stat.tooltip) return [null, new Set()];
    
    const values = stat.tooltipValues?.(stats, allStats) || [];
    const decimal = (typeof stat.decimal === 'function' ? stat.decimal(stats) : stat.decimal) || 0;

    const builder = new TooltipBuilder();
    let bonusSum = 0;
    function addText(line) {
      let bullet = false;
      if (line[0] === '*') {
        line = line.substring(1);
        bullet = true;
      }
      const formatted = replaceReact(line.trim(), /{(?:(\d+)|c(\d+):([^}]*))}/g, (m, index, color, text) => {
        if (index) {
          return values[index];
        } else {
          return colored(text, color);
        }
      });
      builder.line(formatted, bullet);
    }
    const sourceSlots = new Set();
    for (let line of stat.tooltip) {
      let skip = false;
      line = line.replace(/{(\d+)([?!])}/g, (m, index, chr) => {
        if (!values[index]) skip = true;
        return (chr === '!' ? '' : `{${index}}`);
      });
      if (skip) continue;
      const m = line.match(/(.*){sources:([-\w.,/#]+)(?::(\d*)(?:\.(\d+))?([bv%]?))?}/);
      if (m) {
        const sources = m[2].split(',').reduce((prev, cur) => getSources(prev, stats, cur), {});
        if (!sources || !Object.keys(sources).length) continue;
        if (m[1]) addText(m[1]);
        const fmt = {...stat, decimal, plus: false};
        let color = 0;
        if (m[3]) color = parseInt(m[3]);
        if (m[4]) fmt.decimal = parseInt(m[4]);
        if (m[5] === '%') fmt.percent = true;
        if (m[5] === 'v') fmt.percent = false;
        for (const id of sourceOrder) {
          if (sources[id]) {
            if (m[5] === 'b') {
              builder.line(sourceName(id), false);
            } else {
              builder.line(<>{sourceName(id, ': ')}{colored(formatValue(fmt, sources[id]), color || (minval(sources[id]) < 0 ? Color.RED : Color.WHITE))}</>, false);
            }
          }
        }
        fmt.plus = true;
        let inventory = 0;
        for (const id in sources) {
          if (!sourceOrder.includes(id)) {
            if (!skills) {
              const im = id.match(/^inventory(\d+)$/);
              if (im) {
                inventory = addValues(inventory, sources[id]);
              } else {
                if (m[4] === 'b') {
                  const name = sourceName(id);
                  if (name) builder.line(name, false);
                } else {
                  const name = sourceName(id, ': ');
                  if (name) builder.line(<>{name}{colored(formatValue(fmt, sources[id]), color || (minval(sources[id]) < 0 ? Color.RED : Color.BLUE))}</>, false);
                }
              }
            }
            bonusSum = addValues(bonusSum, sources[id]);
            sourceSlots.add(id);
          }
        }
        if (skills) {
          const stats = {};
          for (const id in sources) {
            stats[id] = [`{${sources[id]}}`];
          }
          const lines = statsToLines(stats);
          for (const line of lines) {
            builder.line(colored(replaceReact(line[1], /{(\d+)(?:;\w+)?}/g, (m, value) => colored(value, Color.BLUE)), Color.GOLD2), false);
          }
        }
        if (inventory) {
          if (m[4] === 'b') {
            builder.line(sourceName('inventory'), false);
          } else {
            builder.line(<>{sourceName('inventory', ': ')}{colored(formatValue(fmt, inventory), color || (minval(inventory) < 0 ? Color.RED : Color.BLUE))}</>, false);
          }
        }
      } else {
        let table = null;
        line = line.replace(/{table:(\w+)}(.*){\/table}/, (m, type, cols) => {
          const bp = breakpointList(stats, type);
          if (!bp) {
            table = false;
            return '';
          }
          table = {columns: cols.split('|'), rows: bp.values, value: bp.current, title: bp.title};
          return bp.current;
        });
        if (table === false) continue;
        addText(line);
        if (table) {
          builder.line(<span className="d2p-table-name">{table.title}</span>, false);
          builder.table(table.columns, table.rows, row => row[0] === table.value, 2);
        }
      }
    }

    let color = stat.color ? stat.color(stats) : -1;
    if (color < 0) {
      if (minval(bonusSum) < 0) color = Color.RED;
      else if (minval(bonusSum) > 0) color = Color.BLUE;
      else color = Color.WHITE;
    }

    if (stat.value) {
      builder.header(<>{stat.name}: {colored(formatValue(stat, stat.value(stats), stats), color)}</>);
    } else {
      builder.header(stat.name);
    }

    return [builder.build(), sourceSlots];
  }, [stat, stats, skills, allStats]);

  const setHighlight = EditingContext.useStateSetter("highlightItems");
  React.useLayoutEffect(() => {
    if (unit === 'mercenary') {
      setHighlight([true, sources]);
    } else if (unit === 'player') {
      setHighlight([false, sources]);
    } else {
      setHighlight(null);
    }
    return () => setHighlight(null);
  }, [setHighlight, unit, sources]);

  return tooltip;
}

function TablePopup({ line, stats }) {
  const m = line.match(/{table:(\w+)}(.*){\/table}/);
  if (!m) return null;

  const builder = new TooltipBuilder();
  const [, type, cols] = m;
  const bp = breakpointList(stats, type);
  if (!bp) return null;
  builder.table(cols.split('|'), bp.values, row => row[0] === bp.current, 2);
  return (
    <div className="d2p-tooltip-content" style={{backgroundColor: '#181818'}}>
      {builder.build()}
    </div>
  );
}

function ClickableLine({ line, stats, children, style, ...props }) {
  const isCC = useContentCreator();
  const overlays = React.useContext(OverlayContext);
  const onClick = React.useCallback(e => overlays.popup(e.target, <TablePopup line={line} stats={stats}/>), [overlays, line, stats]);
  if (isCC) {
    props = {onClick, style: {cursor: 'pointer', ...style}, ...props};
  }
  return props ? React.cloneElement(React.Children.only(children), props) : children;
}

function StatLine({ stat, stats, allStats, value, unit }) {
  let color = stat.color ? stat.color(stats) : -1;
  let result = formatValue(stat, value, stats);
  if (color >= 0) result = colored(result, color);
  const hasTable = stat.tooltip?.find(line => line.match(/{table:\w+}/));
  const line = (
    <li className={!value ? "d2p-stat-zero" : null}>
      <span>{stat.name}</span>
      <span>{result}</span>
    </li>
  );
  return (
    <Tooltip tooltip={<StatTooltip stat={stat} stats={stats} allStats={allStats} unit={unit}/>} align="left" disabled={!stat.tooltip}>
      {hasTable ? <ClickableLine line={hasTable} stats={stats}>{line}</ClickableLine> : line}
    </Tooltip>
  );
}

function StatSection({ index, section, search, unit }) {
  const [visible, setVisible] = React.useState(true);
  const onToggle = React.useCallback(() => setVisible(visible => !visible), []);
  const isCC = useContentCreator();
  const profileId = useProfileSetId();

  const allStats = Store.useSelector(selectStats);
  let stats = allStats[unit];
  if (!stats) return null;

  if (section.weapon2) {
    if (!stats.weapon2) return null;
    stats = {...stats, weapon1: stats.weapon2};
  }

  let separator = -1;
  const display = [];
  section.stats.forEach((stat, index) => {
    if (stat.separator) {
      separator = index;
    } else {
      if (stat.classes && !stat.classes.includes(stats.class)) return;
      if (search && !_L(stat.name).toLowerCase().includes(search)) return;
      if (stat.filter && !stat.filter(stats)) return;
      const value = stat.value(stats);
      if (stat.collapse && !value) return;
      if (!search && separator >= 0 && display.length > 0) {
        display.push(<li key={separator} className="d2p-stat-separator"/>);
      }
      separator = -1;
      display.push(<StatLine key={index} stat={stat} stats={stats} allStats={allStats} value={value} unit={unit}/>);
    }
  });

  if (!display.length) return null;

  if (search) {
    return (
      <div className="d2p-StatSection">
        <ul>{display}</ul>
      </div>
    );
  }

  return (
    <Draggable draggableId={section.name} index={index}>
      {provided => (
        <div className="d2p-StatSection" {...provided.draggableProps} ref={provided.innerRef}>
          <h3 onClick={onToggle} {...provided.dragHandleProps}>
            {section.name}
            {!!(section === statList[0] && isCC && profileId) && <EmbedProfileLink {...profileId} type='stats'/>}
          </h3>
          <Accordion visible={visible}>
            <ul>
              {display}
            </ul>
          </Accordion>
        </div>
      )}
    </Draggable>
  );
}

function SearchBox({ value, onChange }) {
  const [focused, setFocus] = React.useState("");
  const onSetValue = React.useCallback(e => onChange(e.target.value), [onChange]);
  const onClear = React.useCallback(() => onChange(""), [onChange]);
  const onFocus = React.useCallback(() => setFocus(true), []);
  const onBlur = React.useCallback(() => setFocus(false), []);
  return (
    <div className="d2p-StatsSearch">
      <input type="text" value={value} onChange={onSetValue} onFocus={onFocus} onBlur={onBlur} placeholder={focused ? null : _L`Search`}/>
      {value.length > 0 && <span className="d2p-search-clear" onClick={onClear}>
        <FontAwesomeIcon icon={faTimesCircle}/>
      </span>}
    </div>
  );
}

const StatsColumn = ({ name, sections, search, unit }) => (
  <Droppable droppableId={name}>
    {provided => (
      <div className="d2p-StatsColumn" {...provided.droppableProps} ref={provided.innerRef}>
        {sections.map((section, idx) => <StatSection key={section.name} index={idx} section={section} search={search} unit={unit}/>)}
        {provided.placeholder}
      </div>
    )}
  </Droppable>
);

function getStatList() {
  return {
    column1: statList.slice(),
    column2: [],
  };
}

export function unitIcon(unit, charClass) {
  if (unit === 'mercenary') {
    return `act${Data.hireling[charClass]?.[0]?.act}`;
  } else if (unit === 'player') {
    return charClass;
  } else if (unit === 'monster') {
    return unit;
  } else if (unit.includes('revive_')) {
    return 'revived';
  } else {
    return charClass;
  }
}

export default React.memo(function Stats() {
  const [search, setSearch] = React.useState('');
  const [dragging, setDragging] = React.useState(false);
  const [columns, setColumns] = React.useState(getStatList);
  const [unit, setUnit] = React.useState('player');
  const stats = Store.useSelector(state => selectStats(state));
  React.useEffect(() => {
    if (!stats[unit]) setUnit('player');
  }, [stats, unit]);

  const onBeforeCapture = React.useCallback(() => {
    setDragging(true);
  }, []);
  const onDragEnd = React.useCallback(({ destination, source }) => {
    setDragging(false);
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;
    setColumns(columns => {
      columns = {...columns};
      columns[source.droppableId] = columns[source.droppableId].slice();
      if (destination.droppableId !== source.droppableId) {
        columns[destination.droppableId] = columns[destination.droppableId].slice();
      }
      const items = columns[source.droppableId].splice(source.index, 1);
      columns[destination.droppableId].splice(destination.index, 0, ...items);
      return columns;
    });
  }, []);

  const unitOptions = React.useMemo(() => Object.entries(stats).map(([id, data]) => ({id, class: data.class, name: data.name})), [stats]);
  const onSetUnit = React.useCallback(value => setUnit(value.id), []);

  const dispatch = Store.useDispatch();
  const onMercRandom = React.useCallback(e => {
    e.stopPropagation();
    dispatch.setProfile(profile => {
      const data = Data.hireling[profile.merc]?.[0];
      if (!data) return profile;
      const i1 = Locale.stringi[data.namefirst];
      const i2 = Locale.stringi[data.namelast];
      if (!i1 || !i2) return profile;
      return {...profile, mercName: Math.floor(Math.random() * (i2 - i1 + 1))};
    });
  }, [dispatch]);

  const renderUnit = React.useCallback(({ id, class: unitId, name }, value) => (<>
    <span><span className={`d2p-class-icon d2p-icon-large d2p-class-${unitIcon(id, unitId)}`}/>{name}</span>
    {value === 'value' && id === 'mercenary' && <FontAwesomeIcon icon={faDice} className="d2p-class-roll" onClick={onMercRandom}/>}
  </>), [onMercRandom]);
  const renderUnitValue = React.useCallback(opt => renderUnit(opt, 'value'), [renderUnit]);
  
  return (
    <React.Fragment>
      <SearchBox value={search} onChange={setSearch}/>
      {Object.keys(stats).length > 1 && (
        <div className="d2p-merc-switch">
          <Select options={unitOptions}
                  value={unitOptions.find(opt => opt.id === unit)}
                  onChange={onSetUnit}
                  renderValue={renderUnitValue}
                  renderOption={renderUnit}
                  className="d2p-ClassSelect"
          />
        </div>
      )}
      <DragDropContext onBeforeCapture={onBeforeCapture} onDragEnd={onDragEnd}>
        <div className={classNames("d2p-StatsList", {"d2p-has-search": search, "d2p-two-column": columns.column2.length > 0 || dragging, "d2p-dragging": dragging})}>
          <div className="d2p-StatsContainer">
            <StatsColumn name="column1" sections={columns.column1} search={search.toLowerCase()} unit={unit}/>
            {(columns.column2.length > 0 || dragging) && <StatsColumn name="column2" sections={columns.column2} search={search.toLowerCase()} unit={unit}/>}
          </div>
        </div>
      </DragDropContext>
    </React.Fragment>
  );
});
